import { useTranslation } from 'react-i18next';

import { FilterBar } from '@/components/shared/FilterBar';
import InfiniteSearchSelect from '@/components/shared/InfiniteSearchSelect';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { SearchSelect } from '@/components/ui/daisy';
import { ComplexDateOutputValue } from '@/components/ui/daisy/templates/complex-date-input/types';
import useExamPendencyFilterChips from '@/data/hook/shared/pendencies/useExamPendencyFilterChips';
import useAuthorizedUnits from '@/data/hook/shared/useAuthorizedUnits';
import useAuth from '@/data/hook/useAuth';
import {
  ExamPendenciesAdvancedFilters,
  ExamPendenciesMainFilters,
} from '@/data/hook/useExamPendenciesFilter';
import { examQueries } from '@/data/query-keys/examQueryKey';
import {
  courseBasesQueryKeys,
  klassesQueryKeys,
  unitsQueryKeys,
  usersQueryKeys,
} from '@/data/services/querykeys';
import { isAdmin, isTeacher } from '@/functions/auth';
import { handleUserFullName } from '@/functions/handleUserFullName';
import Unit from '@/models/_deprecated/Unit';
import { Course } from '@/models/Course';
import Exam from '@/models/Exam';
import Klass, { KlassStatusEnum } from '@/models/Klass';
import User, { UserStatusEnum, UserTypeEnum } from '@/models/User';
import {
  fromComplexDateValue,
  toComplexDateValue,
} from '@/utils/filters/dateRangeParams';
import { translateKlassName } from '@/utils/translateKlassName';

type ExamPendenciesFilterBarProps = {
  mainFilters: ExamPendenciesMainFilters;
  advancedFilters: ExamPendenciesAdvancedFilters;
  advancedFiltersCount: number;
  updateMainFilters: (updated: Partial<ExamPendenciesMainFilters>) => void;
  updateAdvancedFilters: (
    updated: Partial<ExamPendenciesAdvancedFilters>,
  ) => void;
  clearAdvancedFilters: () => void;
};

export default function ExamPendenciesFilterBar({
  mainFilters,
  advancedFilters,
  advancedFiltersCount,
  updateMainFilters,
  updateAdvancedFilters,
  clearAdvancedFilters,
}: ExamPendenciesFilterBarProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'pendencies' });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const { user } = useAuth();
  const userIsTeacher = isTeacher(user?.userType);
  const isAdminUser = isAdmin(user?.userType);
  const { canFilterByUnit, authorizedUnits } = useAuthorizedUnits();

  const chips = useExamPendencyFilterChips({ advancedFilters });

  const dateRangeValue = toComplexDateValue(mainFilters);

  const handleDateRangeChange = (value: ComplexDateOutputValue | null) =>
    updateMainFilters(fromComplexDateValue(value));

  const removeChip = (key: string) =>
    updateAdvancedFilters({ [key]: undefined });

  return (
    <FilterBar>
      <FilterBar.Fields>
        <FilterBar.FieldsGroup>
          <FilterBar.Search
            defaultValue={mainFilters.search}
            placeholder={t('exams.filters.searchPlaceholder')}
            onSearch={search => updateMainFilters({ search })}
          />
          <SearchSelect.Fieldset label={tCommon('klass')} className="flex-1">
            <InfiniteSearchSelect
              listService={klassesQueryKeys.list}
              getService={id => klassesQueryKeys.get(Number(id))}
              filters={{
                teacherId: userIsTeacher && user ? [user.id] : undefined,
                unitId: authorizedUnits,
                status: [KlassStatusEnum.OPEN, KlassStatusEnum.IN_PROGRESS],
              }}
              getOptionLabel={(klass: Klass) => translateKlassName(klass.name)}
              value={mainFilters.klassId ?? null}
              onChange={klass => updateMainFilters({ klassId: klass?.id })}
              placeholder={t('filters.all')}
            >
              <SearchSelect.Option value="">
                {t('filters.all')}
              </SearchSelect.Option>
            </InfiniteSearchSelect>
          </SearchSelect.Fieldset>
          <SearchSelect.Fieldset
            label={t('exams.filters.exam')}
            className="flex-1"
          >
            <InfiniteSearchSelect
              listService={filters => ({
                _ctx: { infinity: examQueries.listInfinite(filters) },
              })}
              getService={id => examQueries.get(Number(id))}
              getOptionLabel={(exam: Exam) => exam.name}
              value={mainFilters.examId ?? null}
              onChange={exam => updateMainFilters({ examId: exam?.id })}
              placeholder={tCommon('select')}
            >
              <SearchSelect.Option value="">
                {t('filters.all')}
              </SearchSelect.Option>
            </InfiniteSearchSelect>
          </SearchSelect.Fieldset>

          <FilterBar.DateInput
            label={t('exams.filters.periodDelivered')}
            value={dateRangeValue}
            onChange={handleDateRangeChange}
          />
        </FilterBar.FieldsGroup>
        <FilterBar.MoreFilters count={advancedFiltersCount} />
      </FilterBar.Fields>

      <FilterBar.Advanced
        className="grid grid-cols-3"
        onClearAll={advancedFiltersCount > 0 ? clearAdvancedFilters : undefined}
      >
        <SearchSelect.Fieldset label={tCommon('course')} className="flex-1">
          <InfiniteSearchSelect
            listService={courseBasesQueryKeys.list}
            getService={id => courseBasesQueryKeys.get(Number(id))}
            getOptionLabel={(course: Course) => course.abbreviation}
            value={advancedFilters.course ?? null}
            onChange={course => updateAdvancedFilters({ course: course?.id })}
            placeholder={t('filters.all')}
          >
            <SearchSelect.Option value="">
              {t('filters.all')}
            </SearchSelect.Option>
          </InfiniteSearchSelect>
        </SearchSelect.Fieldset>

        <ConditionalRenderer condition={isAdminUser}>
          <SearchSelect.Fieldset label={tCommon('teacher')} className="flex-1">
            <InfiniteSearchSelect
              listService={usersQueryKeys.list}
              getService={id => usersQueryKeys.get(Number(id))}
              filters={{
                userType: [UserTypeEnum.TEACHER],
                status: [UserStatusEnum.REGISTRED],
                unitId: authorizedUnits,
              }}
              getOptionLabel={(teacher: User) => handleUserFullName(teacher)}
              value={advancedFilters.teacher ?? null}
              onChange={teacher =>
                updateAdvancedFilters({ teacher: teacher?.id })
              }
              placeholder={t('filters.all')}
            >
              <SearchSelect.Option value="">
                {t('filters.all')}
              </SearchSelect.Option>
            </InfiniteSearchSelect>
          </SearchSelect.Fieldset>
        </ConditionalRenderer>

        <ConditionalRenderer condition={canFilterByUnit}>
          <SearchSelect.Fieldset label={tCommon('unit')} className="flex-1">
            <InfiniteSearchSelect
              listService={unitsQueryKeys.list}
              getService={id => unitsQueryKeys.get(Number(id))}
              filters={{ idIn: authorizedUnits?.toString() }}
              getOptionLabel={(unit: Unit) => unit.name}
              value={advancedFilters.unit ?? null}
              onChange={unit => updateAdvancedFilters({ unit: unit?.id })}
              placeholder={t('filters.all')}
            >
              <SearchSelect.Option value="">
                {t('filters.all')}
              </SearchSelect.Option>
            </InfiniteSearchSelect>
          </SearchSelect.Fieldset>
        </ConditionalRenderer>
      </FilterBar.Advanced>

      <FilterBar.Chips
        chips={chips}
        onRemove={removeChip}
        onClearAll={clearAdvancedFilters}
      />
    </FilterBar>
  );
}
